import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip call when there's no token AND no cookie possibility (avoids 401 noise on landing)
    if (!localStorage.getItem("med_jwt") && !document.cookie.includes("session_token")) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
    // Empty deps array is correct: every identifier inside is either a module
    // import (`api`), a browser global (`localStorage`, `document`), or a
    // useState setter (always stable). Adding them would be redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("med_jwt", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("med_jwt", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("Logout request failed:", err);
    }
    localStorage.removeItem("med_jwt");
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render on unrelated parent updates.
  // All six entries are real consumed values; React docs recommend listing every value
  // referenced. Splitting the context would force every consumer to use two providers
  // for negligible benefit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(
    () => ({ user, setUser, loading, loginWithPassword, register, logout, checkAuth }),
    [user, loading, loginWithPassword, register, logout, checkAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
